import AdminGuard from "@/guards/AdminGuard.tsx";
import { Users } from "@/pages/admin/Users.tsx";
import Explore from "@/pages/explore/Explore.tsx";
import { TrainerUploader } from "@/pages/trainer/TrainerUploader.tsx";
import { ReactNode, StrictMode, useLayoutEffect } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/dates/styles.css";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router";
import { AuthLayout } from "./layouts/auth/AuthLayout";
import AuthRedirect from "./layouts/auth/components/AuthRedirect";
import { Login } from "./pages/auth/Login";
import NotFound from "./pages/NotFound";
import AuthGuard from "./guards/AuthGuard";
import AppLayout from "./layouts/app/AppLayout";
import Dashboard from "./pages/dashboard/Dashboard";
import { Register } from "./pages/auth/Register";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { SSO } from "./pages/auth/SSO";
import { Trainer } from "./pages/trainer/Trainer";
import { Playground } from "./pages/playground/Playground";
import { Editor } from "./pages/editor/Editor";
import { Gallery } from "./pages/gallery/Gallery";
import { Billing } from "@/pages/billing/Billing.tsx";
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://4ae29f080c4837db4af93420a757f0df@o4509076630798336.ingest.de.sentry.io/4509076661534800",
});

const Wrapper = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [location.pathname]);

  return children;
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <Wrapper>
        <Routes>
          <Route element={<AuthGuard />}>
            <Route path="/" element={<AppLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="playground" element={<Playground />} />
              <Route path="editor/:photoName" element={<Editor />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="explore" element={<Explore />} />
              <Route path="characters">
                <Route index element={<Trainer />} />
                <Route
                  path=":loraUuid/uploader"
                  element={<TrainerUploader />}
                />
              </Route>
              <Route path="billing" element={<Billing />} />

              <Route element={<AdminGuard />}>
                <Route path="admin">
                  <Route index element={<Users />} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="/auth" element={<AuthLayout />}>
            <Route
              path="/auth"
              element={<Navigate to="/auth/login" replace />}
            />
            <Route element={<AuthRedirect />}>
              <Route path="login" element={<Login />} />
              <Route path="register" element={<Register />} />
              <Route path="sso" element={<SSO />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="reset-password" element={<ResetPassword />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Wrapper>
    </BrowserRouter>
  </StrictMode>,
);
